'use client';

import { useTranslations } from 'next-intl';
import { DocsLinks } from '@/components/how-it-works/docs-links';
import { InteractiveExplainerButton } from '@/components/how-it-works/interactive-explainer-button';
import { Coins01Icon, IdeaIcon, Rocket01Icon, SparklesIcon } from '@/components/ui/icon/glyphs';
import { InfoModal } from '@/components/ui/info-modal';
import { useSimulatedTradeModalStore } from '@/stores/simulated-trade-modal';

interface ProposalsHowItWorksModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Opens the "What is a proposal?" sub-modal from an inline cross-link. */
  onWhatIsProposal?: () => void;
}

export function ProposalsHowItWorksModal({
  open,
  onOpenChange,
  onWhatIsProposal,
}: ProposalsHowItWorksModalProps): React.ReactNode {
  const t = useTranslations('proposals_how_it_works');
  const openSimulatedTrade = useSimulatedTradeModalStore((s) => s.setOpen);

  return (
    <InfoModal
      open={open}
      onOpenChange={onOpenChange}
      size="lg"
      title={t('title')}
      description={t('description')}
      footer={
        <InfoModal.Footer
          cta={t('got_it')}
          onClose={(): void => onOpenChange(false)}
          lead={
            <InteractiveExplainerButton
              onOpen={(): void => {
                onOpenChange(false);
                openSimulatedTrade(true);
              }}
            />
          }
        />
      }
    >
      <InfoModal.IconList>
        <InfoModal.IconItem
          icon={IdeaIcon}
          title={t('propose_title')}
          description={t('propose_description')}
        />
        <InfoModal.IconItem
          icon={Coins01Icon}
          title={t('back_title')}
          description={t('back_description')}
        />
        <InfoModal.IconItem
          icon={Rocket01Icon}
          title={t('live_title')}
          description={t('live_description')}
        />
        <InfoModal.IconItem
          icon={SparklesIcon}
          title={t('earn_title')}
          description={t('earn_description')}
        />
      </InfoModal.IconList>
      <InfoModal.Callout>{t('info')}</InfoModal.Callout>
      <DocsLinks
        docsPath="/guides/launchpad"
        crossLink={
          onWhatIsProposal !== undefined
            ? { label: t('proposal_link'), onClick: onWhatIsProposal }
            : undefined
        }
      />
    </InfoModal>
  );
}
